// The contact form's messages (src/components/ui/contact-dialog.tsx), checked and turned into an
// email to the site's owner. Shared by the form and the worker (worker/index.ts), which receives
// the request and sends what this returns, through Cloudflare Email Routing.

export const CONTACT_PATH = '/api/contact';
// Email Routing sends only from the site's own domain, to a verified address (wrangler.jsonc)
export const CONTACT_FROM = 'contact@all2scale.com';

export const MESSAGE_TYPES = ['Suggest a set', 'Report a wrong size', 'Support', 'Other'] as const;
export type TMessageType = (typeof MESSAGE_TYPES)[number];

export const MESSAGE_MIN = 10;
export const MESSAGE_MAX = 5000;
const EMAIL_MAX = 254;

export type TContactMessage = { type: TMessageType; email: string; message: string };

export type TContactCheck =
	| { ok: true; message: TContactMessage }
	// A bot that filled in the field people never see: answered as if sent, and dropped
	| { ok: false; reason: 'bot' }
	| { ok: false; reason: 'invalid'; error: string };

// Enough to catch a typo; the reply itself is the real test of an address. No spaces, line breaks
// or characters that mean something in an address header, since it becomes the Reply-To.
const EMAIL_PATTERN = /^[^\s@<>,;"']+@[^\s@<>,;"']+\.[^\s@<>,;"']+$/;

const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

/**
 * Checks what the form sent: `{ type, email, message, website }`. `website` is the hidden field
 * only bots fill in.
 */
export const checkContact = (body: unknown): TContactCheck => {
	if (typeof body !== 'object' || body === null) {
		return { ok: false, reason: 'invalid', error: 'No message' };
	}
	const fields = body as Record<string, unknown>;
	if (text(fields.website) !== '') return { ok: false, reason: 'bot' };

	const type = MESSAGE_TYPES.find((candidate) => candidate === fields.type);
	const email = text(fields.email);
	const message = text(fields.message);
	if (!type) return { ok: false, reason: 'invalid', error: 'Choose what the message is about' };
	if (email.length > EMAIL_MAX || !EMAIL_PATTERN.test(email)) {
		return { ok: false, reason: 'invalid', error: 'Enter a valid email address' };
	}
	if (message.length < MESSAGE_MIN) return { ok: false, reason: 'invalid', error: 'Write a little more' };
	if (message.length > MESSAGE_MAX) {
		return { ok: false, reason: 'invalid', error: 'The message is too long' };
	}

	return { ok: true, message: { type, email, message } };
};

// UTF-8 text as base64, in lines of 76 characters, as email asks
const toBase64Lines = (value: string) => {
	const bytes = new TextEncoder().encode(value);
	let binary = '';
	bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
	return (btoa(binary).match(/.{1,76}/g) ?? []).join('\r\n');
};

// A header value that may hold any character, such as a subject in another script
const encodeHeader = (value: string) =>
	`=?UTF-8?B?${btoa(String.fromCharCode(...new TextEncoder().encode(value)))}?=`;

/**
 * The email for `message`, as raw MIME: from the site, to `to`, answered straight to the visitor.
 * Header values come from checked fields with no line breaks, so none can add a header.
 */
export const buildContactEmail = (
	message: TContactMessage,
	to: string,
	{ id, date }: { id: string; date: Date }
): string => {
	const body = [message.message, '', '--', `Type: ${message.type}`, `From: ${message.email}`].join('\n');

	return [
		`From: All2Scale contact form <${CONTACT_FROM}>`,
		`To: ${to}`,
		`Reply-To: ${message.email}`,
		`Subject: ${encodeHeader(`[All2Scale] ${message.type}`)}`,
		`Message-ID: <${id}@all2scale.com>`,
		`Date: ${date.toUTCString()}`,
		'MIME-Version: 1.0',
		'Content-Type: text/plain; charset=UTF-8',
		'Content-Transfer-Encoding: base64',
		'',
		toBase64Lines(body),
		'',
	].join('\r\n');
};
